import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { useForgotPasswordMutation, useResendCodeMutation, useVerifyCodeMutation } from './services/authenticationApi';
import CircularProgress from '@mui/material/CircularProgress';

const GradientBackground = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
  clipPath: 'polygon(0 0, 51% 0, 39% 100%, 0% 100%)',
  '@media (max-width: 900px)': {
    clipPath: 'none'
  }
}));

interface EmailFormData {
  email: string;
}

interface VerifyFormData {
  verifyCode: string;
}

const ForgotPasswordPage = () => {
  const [showVerifyForm, setShowVerifyForm] = useState(false);
  const emailForm = useForm<EmailFormData>();
  const verifyForm = useForm<VerifyFormData>();
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const [verifyForgotPassword, { isLoading: isVerifying }] = useVerifyCodeMutation();
  const [forgotPasswordToken, setForgotPasswordToken] = useState<string>('');
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(180); // 3 minutes in seconds
  const [resendCode, { isLoading: isResending }] = useResendCodeMutation();
  const emailRequestInProgressRef = useRef(false);
  const verifyRequestInProgressRef = useRef(false);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [isVerifySubmitting, setIsVerifySubmitting] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showVerifyForm && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [showVerifyForm, countdown]);

  // Format seconds to mm:ss
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleEmailSubmit = async (data: EmailFormData) => {
    if (emailRequestInProgressRef.current || isLoading || isEmailSubmitting) return;

    emailRequestInProgressRef.current = true;
    setIsEmailSubmitting(true);
    try {
      const response = await forgotPassword({
        email: data.email,
        Ip: "string" // You might want to get actual IP or handle this differently
      }).unwrap();
      if (response.success) {
        // Store token in state for verification step
        setForgotPasswordToken(response.data.token);
        localStorage.setItem('forgotPasswordToken', response.data.token);
        verifyForm.reset({
          verifyCode: ''
        });
        setShowVerifyForm(true);
        enqueueSnackbar('Doğrulama kodu email adresinize gönderildi', { variant: 'success' });
        setCountdown(180); // Reset timer
        // Başarılı durumda state'leri sıfırlama - kullanıcı verify ekranında
        return;
      }
    } catch (error: any) {
      // Sadece hata durumunda buton tekrar aktif edilir
      setIsEmailSubmitting(false);
      emailRequestInProgressRef.current = false;
      enqueueSnackbar(error?.data?.message || 'Şifre sıfırlama isteği gönderilirken bir hata oluştu.', { variant: 'error' });
    }
  };

  const handleVerifySubmit = async (data: VerifyFormData) => {
    if (verifyRequestInProgressRef.current || isVerifying || isVerifySubmitting) return;

    verifyRequestInProgressRef.current = true;
    setIsVerifySubmitting(true);
    try {
      // Get the token from state first, fallback to localStorage
      const token = forgotPasswordToken || localStorage.getItem('forgotPasswordToken');

      if (!token) {
        enqueueSnackbar('Token bulunamadı. Lütfen tekrar deneyin.', { variant: 'error' });
        setIsVerifySubmitting(false);
        verifyRequestInProgressRef.current = false;
        return;
      }

      const response = await verifyForgotPassword(data.verifyCode).unwrap();

      if (response.success) {
        enqueueSnackbar('Doğrulama başarılı!', { variant: 'success' });
        // Verify başarılı - artık verifyToken kullanılacak, forgotPasswordToken'ı temizle
        localStorage.removeItem('forgotPasswordToken');
        // Changed navigation path to include /authentication prefix
        setTimeout(() => {
          navigate('/reset-password', { replace: true });
        }, 1000);
        // Başarılı durumda state'leri sıfırlama - sayfa değişecek
        return;
      }
    } catch (error: any) {
      // Sadece hata durumunda buton tekrar aktif edilir
      setIsVerifySubmitting(false);
      verifyRequestInProgressRef.current = false;
      console.log(error);
      if (error?.status === 401 || error?.status === 403) {
        navigate('/login', { replace: true });
        return;
      }
      //enqueueSnackbar(error?.data?.message || 'Doğrulama başarısız oldu.', { variant: 'error' });
    }
  };  

  const handleResendCode = async () => {
    try {

      // Get the token from localStorage
      const forgotPasswordData = localStorage.getItem('forgotPasswordToken');
      const token = forgotPasswordData ?? null;

      if (!token) {
        throw new Error('Token not found');
      }

      const response = await resendCode().unwrap();
      if (response.success) {
        setCountdown(180); // Reset timer
        enqueueSnackbar('Yeni kod gönderildi', { variant: 'success' });
      } else {
        enqueueSnackbar(response.message || 'Kod gönderimi başarısız', { variant: 'error' });
      }
    } catch (error: any) {
    /*  enqueueSnackbar(error?.data?.message || 'Kod gönderme işlemi sırasında hata oluştu.', { variant: 'error' }); */
    if (error?.status === 401 || error?.status === 403) {
      navigate('/login', { replace: true });
      return;
    }
    console.error("error", error);
    }
  };


  const renderForm = () => {
    if (showVerifyForm) {
      return (
        <Box sx={{ width: { xs: '100%', md: '80%' } }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '16px', md: '22px' },
              color: 'black',
              mb: 3
            }}
          >
            Doğrulama Kodu
          </Typography>
          <TextField
            label="Doğrulama Kodunu Giriniz"
            variant="outlined"
            fullWidth
            placeholder="Kodu Giriniz..."
            type="text"
            inputProps={{
              minLength: 6,
              maxLength: 6,
              autoComplete: 'off'
            }}
            {...verifyForm.register('verifyCode', {
              required: 'Geçersiz Doğrulama Kodu!',
              minLength: {
                value: 6,
                message: 'Doğrulama kodu 6 karakter olmalıdır'
              }
            })}
            error={!!verifyForm.formState.errors.verifyCode}
            helperText={verifyForm.formState.errors.verifyCode?.message}
          />
          {countdown <= 0 ? (
            <Button
              onClick={handleResendCode}
              disabled={isResending}
              sx={{
                mt: 1,
                width: '100%',
                color: '#1e3c72',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(30, 60, 114, 0.08)'
                }
              }}
            >
              {isResending ? 'Gönderiliyor...' : 'Tekrar Kod Gönder'}
            </Button>
          ) : (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                mt: 1,
                fontSize: { xs: '12px', md: '13px' },
                color: countdown <= 30 ? 'error.main' : 'text.secondary'
              }}
            >
              Kalan Süre: {formatTime(countdown)}
            </Typography>
          )}
          <Button
            type="submit"
            fullWidth
            disabled={isVerifying || isVerifySubmitting || countdown <= 0}
            sx={{
              mt: 3,
              height: { xs: '48px' },
              background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
              borderColor: '#1e3c72',
              borderRadius: '12px',
              color: 'white',
              fontSize: '15px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(30, 60, 114, 0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a3569 0%, #25478a 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(30, 60, 114, 0.35)'
              },
              '&:disabled': {
                background: 'rgba(30, 60, 114, 0.3)',
                color: 'rgba(255, 255, 255, 0.6)'
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {(isVerifying || isVerifySubmitting) ? <CircularProgress size={24} color="inherit" /> : countdown <= 0 ? 'Süre Doldu' : 'Gönder'}
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ width: { xs: '100%', md: '80%' } }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '16px', md: '22px' },
            color: 'black',
            mb: 3
          }}
        >
          Şifrenizi unuttunuz mu?
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mb: 2,
            color: '#666d92',
            fontSize: '14px'
          }}
        >
          Kayıtlı email adresinize doğrulama kodu gönderilecektir.
        </Typography>

        <TextField
          label="E-Mail"
          variant="outlined"
          fullWidth
          placeholder="Email adresinizi giriniz"
          type="email"
          {...emailForm.register('email', {
            required: 'Geçersiz Email Adresi!',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Geçersiz email formatı"
            }
          })}
          error={!!emailForm.formState.errors.email}
          helperText={emailForm.formState.errors.email?.message}
        />

        <Button
          type="submit"
          fullWidth
          disabled={isLoading || isEmailSubmitting}
          sx={{
            mt: 3,
            width: '100%',
            height: { xs: '48px' },
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            borderColor: '#1e3c72',
            borderRadius: '12px',
            color: 'white',
            fontSize: '15px',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 16px rgba(30, 60, 114, 0.25)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1a3569 0%, #25478a 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(30, 60, 114, 0.35)'
            },
            '&:disabled': {
              background: 'rgba(30, 60, 114, 0.3)',
              color: 'rgba(255, 255, 255, 0.6)'
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {(isLoading || isEmailSubmitting) ? <CircularProgress size={24} color="inherit" /> : 'Gönder'}
        </Button>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100vw',
        height: '100vh',
      }}
    >
      <GradientBackground />

      <Paper
        component="form"
        onSubmit={showVerifyForm ? verifyForm.handleSubmit(handleVerifySubmit) : emailForm.handleSubmit(handleEmailSubmit)}
        elevation={3}
        sx={{
          width: { xs: '80%', xl: '51.4%' },
          maxWidth: '984px',
          padding: { xs: 0, md: 5 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 20,
          bgcolor: 'rgba(255, 255, 255, 0.5)',
          borderRadius: '35px',
          border: '1.2px solid rgba(0, 0, 0, 0.1)',
          gap: { xs: 2, md: 5 }
        }}
      >
        <Box
          sx={{
            width: { xs: '60%', md: '41%' },
            p: 2.5,
            color: 'white'
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/assets/images/logo/newlogo.png`}
            alt="Logo"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center'
            }}
          />
        </Box>

        <Box
          sx={{
            width: { xs: '100%', md: '59%' },
            bgcolor: { xs: 'white', md: 'transparent' },
            p: { xs: 2.5, md: 0 },
            borderBottomLeftRadius: { xs: '35px', md: 0 },
            borderBottomRightRadius: { xs: '35px', md: 0 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3
          }}
        >
          {renderForm()}
        </Box>
      </Paper>

    </Box>
  );
};

export default ForgotPasswordPage;
